import { useState, useCallback } from 'react';
import { IRecipe } from '../api/IRecipe';
import { generateAIRecipe, AIRecipeResponse } from '../api/aiRecipe';

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export function useAIRecipe() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [proposal, setProposal] = useState<Partial<IRecipe> | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);

  const handleResponse = useCallback((resp: AIRecipeResponse, userMsg: string) => {
    setMessages(prev => [
      ...prev,
      { role: 'user', content: userMsg },
      { role: 'assistant', content: resp.message },
    ]);
    if (resp.proposal) {
      setProposal(resp.proposal);
    }
    if (resp.error) {
      setError(resp.error.detail);
    }
  }, []);

  const submit = useCallback(async (text: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const resp = await generateAIRecipe({
        action: 'generate',
        text,
        conversation_history: messages,
      });
      handleResponse(resp, text);
      return resp.proposal;
    } catch (e: any) {
      const msg = e.response?.data?.message || e.message || 'An error occurred';
      setError(msg);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [messages, handleResponse]);

  const refine = useCallback(async (currentRecipe: Partial<IRecipe>, instructions: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const resp = await generateAIRecipe({
        action: 'refine',
        current_recipe: currentRecipe,
        refinement_instructions: instructions,
        conversation_history: messages,
      });
      handleResponse(resp, instructions);
      return resp.proposal;
    } catch (e: any) {
      const msg = e.response?.data?.message || e.message || 'An error occurred';
      setError(msg);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [messages, handleResponse]);

  const reset = useCallback(() => {
    setMessages([]);
    setProposal(null);
    setError(null);
    setIsLoading(false);
  }, []);

  return {
    submit,
    refine,
    reset,
    isLoading,
    error,
    proposal,
    messages,
  };
}
